import DownloadCSVMenuButton, {
    downloadTypes,
} from 'components/Button/DownloadCSVMenuButton';
import {
    GOOGLE_METADATA_BY_KEY,
    GOOGLE_SORT_OPTIONS,
    INSTAGRAM_METADATA_BY_KEY,
    POPULARITY_METADATA_BY_KEY,
    TIKTOK_METADATA_BY_KEY,
} from 'utils/metrics';
import {
    clusterFilterOptions,
    filterAddedOn,
    filterClusters,
    filterHealthStats,
    filterLifeCycle,
    filterNames,
    healthStatFilterOptions,
    instagramClusterFilterOptions,
    lifeCycleFilterOptions,
    tikTokClusterFilterOptions,
} from 'utils/filters';
import { downloadItemsAsCSV, downloadTimeSeriesAsCSV } from 'utils/formatCSV';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
    useFilterSearchParams,
    useOnUpdate,
    useSortSearchParams,
} from 'utils/hooks';
import Card from './Card';
import { CardSkeletonItem } from './CardSkeleton';
import CardsFilterAccordion from 'components/CardsFilterAccordion';
import Loading from './Loading';
import SearchInput from './DataVis/DataTable/SearchInput';
import SortSelect from 'components/Input/SortSelect';
import Stack from './Stack';
import objectHash from 'object-hash';
import sortDataByKey from 'utils/sortDataByKey';
import useStaleFilterNotice from 'utils/hooks/useStaleFilterNotice';
import styled from 'styled-components';
import { useWindowVirtualizer } from '@tanstack/react-virtual';

const PADDING_SIZE = 20;
const CARD_WIDTH = 237;
const CARD_HEIGHT = 240;

export const Wrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

export const defaultSortOptions = [
    GOOGLE_SORT_OPTIONS.volume,
    GOOGLE_SORT_OPTIONS.absoluteGrowth,
    GOOGLE_SORT_OPTIONS.YoY,
    GOOGLE_SORT_OPTIONS.QoQ_increase,
    GOOGLE_SORT_OPTIONS.QoQ,
    GOOGLE_SORT_OPTIONS.MoM_increase,
    GOOGLE_SORT_OPTIONS.MoM,
];

const filterSortKeyAccesor = (sortOption) => sortOption.value;

function useContainerWidth(ref) {
    const [width, setWidth] = useState(0);
    useEffect(() => {
        if (!ref.current) return;
        const ro = new ResizeObserver(([entry]) =>
            setWidth(entry.contentRect.width),
        );
        ro.observe(ref.current);
        return () => ro.disconnect();
    }, []);
    return width;
}

const CardCell = memo(function CardCell({
    card,
    sortOption,
    onCardClick,
    healthStatKeys,
    CardComponent,
    filters,
    parentCategory,
}) {
    if (!card) return null;
    return (
        <CardComponent
            sortOption={sortOption}
            onCardClick={onCardClick}
            healthStatKeys={healthStatKeys}
            filters={filters}
            parentCategory={parentCategory}
            {...card}
        />
    );
});

export const defaultFilterAccessorsAndOptions = {
    cluster: {
        accessor: filterSortKeyAccesor,
        options: clusterFilterOptions,
    },
    tikTokCluster: {
        accessor: filterSortKeyAccesor,
        options: tikTokClusterFilterOptions,
    },
    instagramCluster: {
        accessor: filterSortKeyAccesor,
        options: instagramClusterFilterOptions,
    },
    life_cycle: {
        accessor: filterSortKeyAccesor,
        options: lifeCycleFilterOptions,
    },
    healthStats: {
        accessor: filterSortKeyAccesor,
        options: healthStatFilterOptions.map(({ options }) => options).flat(),
    },
};
export const defaultHandleFilter = {
    addedOn: filterAddedOn,
    cluster: filterClusters,
    tikTokCluster: filterClusters,
    instagramCluster: filterClusters,
    life_cycle: filterLifeCycle,
    healthStats: filterHealthStats,
    name: filterNames,
};
const defaultInitialSortOption = defaultSortOptions[0];

export const supportedPrimaryMetrics = [
    GOOGLE_METADATA_BY_KEY.volume,
    TIKTOK_METADATA_BY_KEY.non_cum_avg_weekly_views,
    POPULARITY_METADATA_BY_KEY.popularity_index_avg_12_month,
    INSTAGRAM_METADATA_BY_KEY.non_cum_avg_weekly_likes,
];

export const useCardsFilterSort = ({
    rootCategoryId,
    handleFilter = defaultHandleFilter,
    initialSortOption = defaultInitialSortOption,
    // Keep entities that lack a value for every supported primary metric
    // (Google volume, TikTok views, popularity index, Instagram likes)
    // rather than dropping them from the card list.
    filterMissingData = false,
    data,
    sortOptions = defaultSortOptions,
    handleSort = sortDataByKey,
    sortOptionKeyAccessor = filterSortKeyAccesor,
    filterAccessorsAndOptions = defaultFilterAccessorsAndOptions,
}) => {
    const cards = data
        ? data.reduce((acc, { time_series: timeSeries, ...card }) => {
              const hasDefinedPrimaryMetric = supportedPrimaryMetrics.some(
                  ({ key }) => card[key] || card[key] === 0,
              );
              if (filterMissingData && !hasDefinedPrimaryMetric) return acc;
              acc.push({ ...card, timeSeries });
              return acc;
          }, [])
        : null;

    const cannotComputeCount =
        cards &&
        cards.length < 1 &&
        data.some(({ fetching, fetchingData }) => fetching || fetchingData);

    const {
        filteredData,
        filters,
        filtersKey,
        setFilter,
        setFilters,
        resetFilters,
        staleFilters,
    } = useFilterSearchParams({
        data: cards,
        handleFilter,
        initialFilterOption: {},
        filterAccessorsAndOptions,
    });
    useStaleFilterNotice(staleFilters);

    const nameFilter =
        filters && filters.name && filters.name.length ? filters.name[0] : null;

    const onSearchInputChange = useCallback(
        (value) => {
            try {
                if (value && value !== nameFilter) {
                    setFilter('name', [value]);
                } else if (!value && nameFilter) {
                    setFilter('name', []);
                }
            } catch (err) {
                console.error(err);
            }
        },
        [nameFilter, setFilter],
    );

    const {
        setSortOption,
        sortOption,
        sortOptionKey,
        sortedData,
        maxSortMetric,
    } = useSortSearchParams({
        data: filteredData,
        handleSort,
        initialSortOption,
        options: sortOptions,
        sortOptionKeyAccessor,
    });

    useOnUpdate(() => {
        setSortOption(initialSortOption);
        resetFilters();
    }, [rootCategoryId]);

    return {
        filteredData,
        filters,
        filtersKey,
        maxSortMetric,
        onSearchInputChange,
        setFilter,
        setFilters,
        resetFilters,
        setSortOption,
        sortOption,
        sortOptionKey,
        sortedData,
        initialSortOption,
        totalCards: !cards || cannotComputeCount ? null : cards.length,
    };
};

export function CardsWrapper({
    noDataMessage,
    filters,
    sortOption,
    isLoading,
    data,
    onCardClick,
    rowHeight = CARD_HEIGHT,
    cardWidth = CARD_WIDTH,
    paddingSize = PADDING_SIZE,
    LoadingComponent = Loading,
    CardComponent = Card,
    healthStatKeys,
    isViewingCategories,
    parentCategory,
    skeletonCount = 0,
}) {
    const containerRef = useRef(null);
    const width = useContainerWidth(containerRef);

    const columnCount = Math.max(
        1,
        Math.floor(width / (cardWidth + paddingSize)),
    );
    const columnWidth = width > 0 ? Math.floor(width / columnCount) : cardWidth;
    const dataLength = data?.length ?? 0;
    const totalItems = dataLength + skeletonCount;
    const rowCount = Math.ceil(totalItems / columnCount);

    const virtualizer = useWindowVirtualizer({
        count: rowCount,
        estimateSize: () => rowHeight,
        overscan: 5,
        scrollMargin: containerRef.current?.offsetTop ?? 0,
    });

    const filtersHash = objectHash(filters);
    useEffect(() => {
        if (
            containerRef.current &&
            containerRef.current.getBoundingClientRect().top < 0
        ) {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, [sortOption, filtersHash]);

    return (
        <div ref={containerRef}>
            {isLoading && !data?.length ? (
                <div css="display: flex; justify-content: center;">
                    {isViewingCategories ? (
                        <div css="width: 100%;">
                            <LoadingComponent
                                cardHeightPx={rowHeight - paddingSize}
                            />
                        </div>
                    ) : (
                        <div css="width: 300px; max-width: 100%; padding: 16px 0;">
                            <LoadingComponent />
                        </div>
                    )}
                </div>
            ) : null}
            {!isLoading && data && data.length < 1 ? (
                <div css="text-align: center;">{noDataMessage}</div>
            ) : null}
            {data?.length > 0 && width > 0 && (
                <div
                    style={{
                        height: virtualizer.getTotalSize(),
                        width: '100%',
                        position: 'relative',
                    }}
                >
                    {virtualizer.getVirtualItems().map((virtualRow) => (
                        <div
                            key={virtualRow.key}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: virtualRow.size,
                                transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    gap: paddingSize,
                                }}
                            >
                                {Array.from({ length: columnCount }).map(
                                    (_, colIndex) => {
                                        const itemIndex =
                                            virtualRow.index * columnCount +
                                            colIndex;
                                        const isSkeleton =
                                            itemIndex >= dataLength &&
                                            itemIndex < totalItems;
                                        const card = data?.[itemIndex];
                                        return (
                                            <div
                                                key={colIndex}
                                                style={{
                                                    flex: 1,
                                                    height:
                                                        rowHeight - paddingSize,
                                                    position: 'relative',
                                                }}
                                            >
                                                {isSkeleton ? (
                                                    <CardSkeletonItem />
                                                ) : (
                                                    <CardCell
                                                        card={card}
                                                        sortOption={sortOption}
                                                        onCardClick={
                                                            onCardClick
                                                        }
                                                        healthStatKeys={
                                                            healthStatKeys
                                                        }
                                                        CardComponent={
                                                            CardComponent
                                                        }
                                                        filters={filters}
                                                        parentCategory={
                                                            parentCategory
                                                        }
                                                    />
                                                )}
                                            </div>
                                        );
                                    },
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export function CardsFilterSidePanel({
    filters,
    setFilter,
    setFilters,
    resetFilters,
    sortOption,
    setSortOption,
    initialSortOption,
    onSearchInputChange,
    rootCategoryId,
    data,
    noDataMessage,
    isLoading,
    mixpanelMeta,
    dataReady,
    parentName,
    country,
    name,
    healthStatKeys,
    ...restProps
}) {
    const defaultSearchInputValue =
        filters.name && filters.name[0] ? filters.name[0] : '';
    return (
        <div
            css={`
                display: grid;
                grid-template-columns: 287px 1fr;
                grid-column-gap: 12px;
                padding-bottom: 240px;
            `}
        >
            <div>
                <CardsFilterAccordion
                    filters={filters}
                    setFilter={setFilter}
                    resetFilters={() => {
                        const { name } = filters;
                        if (name) {
                            setFilters({ name });
                        } else {
                            resetFilters();
                        }
                    }}
                    mixpanelMeta={mixpanelMeta}
                    healthStatKeys={healthStatKeys}
                />
            </div>
            <div>
                <Stack
                    $space="8px"
                    $direction="row"
                    css={`
                        display: flex;
                        justify-content: space-between;
                        margin: 0 ${PADDING_SIZE / 2}px 10px;
                    `}
                >
                    <SearchInput
                        // TODO: support multiple name filters
                        key={defaultSearchInputValue}
                        defaultValue={defaultSearchInputValue}
                        onSearchInputChange={onSearchInputChange}
                        name={name}
                        mixpanelMeta={mixpanelMeta}
                    />
                    <SortSelect
                        options={defaultSortOptions}
                        value={sortOption}
                        onChange={(selected) => setSortOption(selected)}
                        mixpanelContext="CardsSortSelect"
                    />
                    <DownloadCSVMenuButton
                        isDisabled={!dataReady}
                        isLoading={isLoading}
                        options={[
                            {
                                mixpanelMeta: { label: 'card data csv' },
                                children: 'Card Data CSV',
                                onClick: () =>
                                    downloadItemsAsCSV(
                                        `${parentName} (${country ? country.code || country.name : null}) trend data`,
                                        data,
                                    ),
                                downloadType: downloadTypes.CSV,
                                key: 'card data csv',
                            },
                            {
                                mixpanelMeta: { label: 'time series csv' },
                                children: 'Time Series CSV',
                                onClick: () =>
                                    downloadTimeSeriesAsCSV(
                                        `${parentName} (${country ? country.code || country.name : null}) time series data`,
                                        data,
                                    ),
                                downloadType: downloadTypes.CSV,
                                key: 'time series csv',
                            },
                        ]}
                        mixpanelMeta={mixpanelMeta}
                    />
                </Stack>
                <CardsWrapper
                    key={rootCategoryId}
                    data={data}
                    filters={filters}
                    sortOption={sortOption}
                    noDataMessage={noDataMessage}
                    isLoading={isLoading}
                    healthStatKeys={healthStatKeys}
                    {...restProps}
                />
            </div>
        </div>
    );
}
